/* eslint-disable complexity */
import { useRef, RefObject } from 'react';

import classNames from 'classnames';

import { useIsDesktop } from '../../../hooks';
import { Box } from '../../../Box';
import { Button } from '../../../Button';
import { Icon } from '../../../Icon';
import { Typography } from '../../../Typography';
import type { FileInputProps } from '../FileInput.intefaces';
import type { FileInputViewProps } from '../FileInputView.interfaces';
import { FillBanner } from '../FillBanner';
import { FileInputPreviewLoading } from '../FileInputPreviewLoading';
import { FileInputPreviewDocument } from '../FileInputPreviewDocument';
import useStyles from '../FileInput.styles';

interface FileInputPreviewProps
  extends
    FileInputViewProps,
    Pick<
      FileInputProps,
      'error' | 'bannerText' | 'loadingText' | 'placeholder'
    > {
  blobDocument: RefObject<Blob | null>;
  allowedTypes: string[];
  autorecognitionQr: boolean;
  onRotate: () => void;
}

export const FileInputPreview = ({
  value,
  disabled,
  fullWidth,
  inputId,
  testId,
  inputLabel,
  errorDisplay,
  finalError,
  isLoading,
  isDisabled,
  inputProps,
  getRootProps,
  getInputProps,
  onLoad,
  onRemove,
  error,
  bannerText,
  loadingText,
  placeholder,
  blobDocument,
  allowedTypes,
  autorecognitionQr,
  onRotate,
}: FileInputPreviewProps) => {
  const { classes } = useStyles();
  const isDesktop = useIsDesktop();
  const previewBlock = useRef<HTMLDivElement>(null);

  const previewWidth = 296;
  let previewHeight;

  if (isDesktop || autorecognitionQr || (value && !error && !isLoading)) {
    previewHeight = 418;
  } else if (bannerText && !value) {
    previewHeight = 144;
  } else {
    previewHeight = 92;
  }

  const previewBlockRect = previewBlock?.current?.getBoundingClientRect();
  const previewBlockWidth = previewBlockRect?.width ?? previewWidth;

  const previewResponsiveHeight =
    (previewHeight * previewBlockWidth) / previewWidth;

  const height =
    fullWidth && !autorecognitionQr ? previewResponsiveHeight : previewHeight;

  return (
    <Box width={fullWidth ? '100%' : previewWidth} testId={testId}>
      {inputLabel}

      <Box position="relative">
        {isLoading ? (
          <FileInputPreviewLoading
            height={height}
            hasValue={!!value}
            isDesktop={isDesktop}
            autorecognitionQr={autorecognitionQr}
            bannerText={bannerText}
            loadingText={loadingText}
          />
        ) : (
          <div {...getRootProps({ className: 'dropzone' })}>
            <input
              id={inputId}
              {...inputProps}
              {...getInputProps()}
              data-testid={`${testId}-input`}
            />
            <div
              ref={previewBlock}
              className={classNames(
                classes.previewDropzone,
                finalError && classes.error,
                isDisabled && classes.disabled,
              )}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={height}
              >
                {bannerText && !value && <FillBanner>{bannerText}</FillBanner>}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width={{ xs: 154, md: 200 }}
                  mt={!isDesktop && bannerText && !value ? '56' : '0'}
                >
                  <Box
                    mb="4"
                    height={isDesktop || autorecognitionQr ? 56 : 32}
                    width={isDesktop || autorecognitionQr ? 56 : 32}
                    fontSize={{ xs: autorecognitionQr ? 56 : 32, md: 56 }}
                  >
                    <Icon name="vkhrtek_file" size="48" />
                  </Box>
                  <Box
                    mb={{ xs: autorecognitionQr ? '12' : '0', md: '12' }}
                    color={
                      isDesktop || autorecognitionQr
                        ? 'text.light.tertirary'
                        : 'original.brand.primary'
                    }
                  >
                    <Typography
                      display="block"
                      variant={
                        isDesktop || autorecognitionQr ? 'body2' : 'caption'
                      }
                      color="inherit"
                      align="center"
                    >
                      {placeholder || 'Загрузите изображение или PDF файл'}
                    </Typography>
                  </Box>
                  {(isDesktop || autorecognitionQr) && (
                    <Button
                      variant="secondary"
                      size="small"
                      disabled={disabled}
                      testId={`${testId}-upload`}
                    >
                      Загрузить
                    </Button>
                  )}
                </Box>
              </Box>
            </div>
          </div>
        )}
        {value && !finalError && !isLoading && (
          <FileInputPreviewDocument
            blobDocument={blobDocument}
            allowedTypes={allowedTypes}
            isDesktop={isDesktop}
            disabled={disabled}
            testId={testId}
            onLoad={onLoad}
            onRemove={onRemove}
            onRotate={onRotate}
          />
        )}
      </Box>
      {finalError && errorDisplay}
    </Box>
  );
};
